// KAPCSOLÓDÓ REKORDOK tömör listája egy detail-/form-oldal szekciójában (fiókok, szolgáltatások, számlázási
// profilok, bankkártyák egy ügyfél alatt).
//
// MIÉRT NEM a `DataTable`: az a LISTA-OLDALAK primitívje — saját kártya-felülettel, oszlop-fejlécekkel, kötött
// magassággal és belső görgetéssel. Egy detail-oldalon négy ilyen egymás alatt „oldal az oldalban" hatást kelt:
// négy külön háttér-doboz, négy fejléc-sor, négy görgetősáv — a felület szétesik, és a lényeg (az alapadat-form)
// elvész. A kapcsolódó lista NEM önálló munkaterület, hanem a szülő rekord egy tulajdonsága → **háttér nélküli**,
// hairline-nal tagolt, 3 információt mutató sorok. A részletekért a sor a saját oldalára/sheetjére visz.
//
// A SOR HÁROM SLOTJA (szándékosan kevés — ez a „ne lista-oldal" szabály lényege):
//   • `title`    — az azonosító (mi ez a rekord), opcionális `subtitle` másodsorral,
//   • `meta`     — EGY kiegészítő információ, halványan (cím, bank, gyakoriság…),
//   • `status`   — jobbra zárt állapot-jel (badge) vagy semmi.
// Ami ezen felül van (ikon-badge-ek, kapcsolattartó-chipek, darabszám, weboldal-link), az a lista-oldalé.
//
// A NEGYEDIK, OPCIONÁLIS SLOT — `actions` (a fiók-oldal kötés-listái): sor-szintű ikon-akciók a
// jobb szélen. CSAK akkor indokolt, ha a rekordnak nincs saját oldala/lista-oldala, ahol kezelhető lenne. A
// fiók-kötés (ügyféladminisztrátor / kapcsolattartó) pont ilyen: kizárólag a fiók-detailen létezik, tehát a
// szerkesztés/eltávolítás sem tehető máshova. A slot MEGADÁSA kizárja a sor-kattintást (`onItemClick`) az adott
// soron: gomb nem ágyazható gombba (érvénytelen HTML, és a billentyűzetes fókusz is összeomlana).
//
// ELRENDEZÉS: grid. Desktopon egy sor három hasábbal; mobilon a `meta` a második sorba csúszik, a `status`
// pedig a cím mellé jobbra — így keskeny kijelzőn sem törik el és nem kell vízszintesen görgetni.
import type { ReactNode } from 'react';

import { SectionPlaceholder, type SectionIcon } from '@/components/shared/section-placeholder';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';

/** Alapértelmezett sor-plafon: elég ahhoz, hogy a tipikus eset TELJESEN látszódjon, de ne nyúljon el az oldal. */
const DEFAULT_MAX_VISIBLE = 8;

export interface RelatedListItem {
  id: string;
  /** Az azonosító szöveg (a rekord neve) — a sor „anchor"-ja. */
  title: ReactNode;
  /** Opcionális másodsor a cím alatt (halvány, kisebb) — pl. csomag-név a szolgáltatás alatt. */
  subtitle?: ReactNode;
  /** EGY kiegészítő információ (cím, bank, gyakoriság). Desktopon középső hasáb, mobilon a 2. sor. */
  meta?: ReactNode;
  /** Jobbra zárt állapot-jel (badge) — ha nincs, a hasáb összecsukódik. */
  status?: ReactNode;
  /**
   * Sor-szintű akciók (ikon-gombok) a jobb szélen — lásd a fájl fejléc-kommentjét. Ha meg van adva, a sor NEM
   * kattintható (a `onItemClick` erre a sorra nem érvényesül), mert gomb nem ágyazható gombba.
   */
  actions?: ReactNode;
}

export function RelatedList({
  items,
  maxVisible = DEFAULT_MAX_VISIBLE,
  onShowAll,
  showAllLabel,
  footerAction,
  isLoading = false,
  isError = false,
  forbidden = false,
  onRetry,
  onItemClick,
  emptyLabel,
  emptyIcon,
  errorLabel,
  forbiddenLabel,
  retryLabel,
  itemAriaLabel,
}: {
  items: readonly RelatedListItem[];
  /**
   * Hány sor látszik, mielőtt a lista CSONKUL. A kapcsolódó szekció nem munkaterület: egy ügyfélnek lehet 87
   * szolgáltatás-hozzárendelése, és ha mind kirenderel, a felhasználó több ezer pixelt görget, mire a
   * következő szekcióhoz ér. (Ez a `DataTable` kötött magasságú, belül görgethető dobozának a szerepe volt —
   * a csere során a korlát elveszett, ezért kell ide.) A csonkolás SOSEM néma: a `footer` megmondja, mennyi
   * maradt ki, és hol nézhető meg a teljes lista.
   */
  maxVisible?: number;
  /** Csonkolás esetén ide navigálunk („összes megtekintése"). Hiányában a csonkolás CSAK szöveggel jelenik meg. */
  onShowAll?: (() => void) | undefined;
  /** A csonkolás-sáv felirata; a hívó a TELJES darabszámot kapja meg (pl. „Mind a 87 megtekintése"). */
  showAllLabel: (total: number) => string;
  /**
   * MINDIG látszó láb-link („Részletes megtekintés") a szűrt LISTA-OLDALRA — nem a csonkolás következménye.
   *
   * MIÉRT KÜLÖN A CSONKOLÁS-SÁVTÓL (user-döntés): ahol van hova vinni, ott a szekció a TELJES halmazt
   * kiírja (`maxVisible: Infinity`), és a láb-link nem azt mondja, hogy „maradt ki valami", hanem hogy „a
   * szűrők, oszlopok és tömeges műveletek a lista-oldalon vannak". A két sáv kizárja egymást: ha nincs
   * csonkolás, a `showAllLabel` nem is renderel.
   */
  footerAction?: { label: string; onClick: () => void } | undefined;
  isLoading?: boolean;
  isError?: boolean;
  /** 401/403 — ilyenkor NINCS „újra" gomb: az újrapróbálás ugyanúgy elbukna, csak zajt csinálna. */
  forbidden?: boolean;
  onRetry?: (() => void) | undefined;
  /** Ha meg van adva, a sorok kattinthatók (a rekord saját nézetére visznek). */
  onItemClick?: ((item: RelatedListItem) => void) | undefined;
  emptyLabel: string;
  /**
   * A szekció tárgyának ikonja az ÜRES állapot szaggatott dobozához — UGYANAZ, amit a létrehozás-oldal
   * zárolt szekciója mutat, hogy a két állapot egy nyelvet beszéljen (`SectionPlaceholder`).
   */
  emptyIcon: SectionIcon;
  errorLabel: string;
  forbiddenLabel: string;
  retryLabel: string;
  /** A kattintható sor akadálymentes neve (a látható cím ReactNode is lehet). */
  itemAriaLabel?: ((item: RelatedListItem) => string) | undefined;
}): JSX.Element {
  if (isLoading) {
    // Három csontváz-sor: annyi, hogy a szekció magassága ne ugorjon nagyot a betöltés végén.
    return (
      <div className="space-y-3 py-1" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-5 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-wrap items-center gap-3 py-1">
        <p className="text-muted-foreground text-sm">{forbidden ? forbiddenLabel : errorLabel}</p>
        {!forbidden && onRetry && (
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            {retryLabel}
          </Button>
        )}
      </div>
    );
  }

  if (items.length === 0) {
    // Az üres szekció UGYANAZT a szaggatott dobozt kapja, mint a létrehozás-oldal zárolt szekciója — csak
    // LAKAT NÉLKÜL: itt nincs teljesítendő feltétel, a fejléc „hozzáadás" gombja azonnal kiút. (Korábban ez
    // egy sor halvány szöveg volt: ugyanaz az információ két különböző vizuális nyelven.)
    return <SectionPlaceholder icon={emptyIcon}>{emptyLabel}</SectionPlaceholder>;
  }

  const visible = items.slice(0, maxVisible);

  return (
    <>
      <ul className="divide-border/60 divide-y">
        {visible.map((item) => {
          // A negyedik (`actions`) hasáb ÜRESEN 0 szélességű — a slot nélküli hívók elrendezése változatlan.
          // A STÁTUSZ-OSZLOP minimum-szélessége FIX (`minmax(8rem,auto)`): minden sor KÜLÖN grid, tehát egy `auto`
          // oszlop soronként más szélességű — a szélesebb badge („függőben") a `fr` oszlopokat beljebb tolta, és a
          // `meta` (pl. a gyakoriság) soronként elcsúszott (user-lelet).
          const row = (
            <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-3 gap-y-0.5 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(8rem,auto)_auto]">
              <div className="col-start-1 row-start-1 min-w-0">
                <Text variant="itemTitle" as="span" className="block truncate">
                  {item.title}
                </Text>
                {item.subtitle !== undefined && item.subtitle !== null && (
                  <Text tone="tertiary" as="span" className="block truncate text-xs">
                    {item.subtitle}
                  </Text>
                )}
              </div>
              {item.meta !== undefined && item.meta !== null && (
                <div className="text-muted-foreground col-span-3 col-start-1 row-start-2 min-w-0 truncate text-sm sm:col-span-1 sm:col-start-2 sm:row-start-1">
                  {item.meta}
                </div>
              )}
              {item.status !== undefined && item.status !== null && (
                <div className="col-start-2 row-start-1 shrink-0 justify-self-end sm:col-start-3">
                  {item.status}
                </div>
              )}
              {item.actions !== undefined && item.actions !== null && (
                <div className="col-start-3 row-start-1 shrink-0 justify-self-end sm:col-start-4">
                  {item.actions}
                </div>
              )}
            </div>
          );
          // A sor-akciós sor SOSEM gomb (nested button) — a kattintást a saját ikon-gombjai adják.
          return (
            <li key={item.id}>
              {onItemClick && item.actions === undefined ? (
                // A kattintható sor GOMB (nem `div onClick`): billentyűzettel fókuszálható és Enterrel aktiválható.
                // A hover-felület 8px-cel túlnyúlik a hairline-on (`-mx-2` + kompenzált szélesség), hogy a
                // kiemelés ne szorítsa a szöveget a széléhez — a választóvonal viszont a tartalom szélén marad.
                <button
                  type="button"
                  onClick={() => onItemClick(item)}
                  aria-label={itemAriaLabel?.(item)}
                  className="hover:bg-accent/40 focus-visible:ring-ring -mx-2 w-[calc(100%+1rem)] rounded-md px-2 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2"
                >
                  {row}
                </button>
              ) : (
                <div className="py-2.5">{row}</div>
              )}
            </li>
          );
        })}
      </ul>
      {/* CSONKOLÁS-SÁV — csak ha tényleg maradt ki sor. A darabszám a TELJES halmazé, nem a rejtetté: a
          felhasználót az érdekli, mennyi van összesen, nem az, hogy mennyit nem lát. */}
      {items.length > maxVisible &&
        (onShowAll ? (
          <div className="pt-2">
            <Button type="button" variant="link" size="sm" className="px-0" onClick={onShowAll}>
              {showAllLabel(items.length)}
            </Button>
          </div>
        ) : (
          <p className="text-muted-foreground pt-2.5 text-sm">{showAllLabel(items.length)}</p>
        ))}
      {/* RÉSZLETES MEGTEKINTÉS — a szűrt lista-oldal belépője, csonkolástól függetlenül (l. `footerAction`). */}
      {footerAction && (
        <div className="pt-2">
          <Button
            type="button"
            variant="link"
            size="sm"
            className="px-0"
            onClick={footerAction.onClick}
          >
            {footerAction.label}
          </Button>
        </div>
      )}
    </>
  );
}
